import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import jobWorksApi, {
  IJobWorkType,
  CreateJobWorkDTO
} from '../api/jobWorks';
import { getJobWorkTypes, getVendors, getJobWorkGarmentParts } from '../services/api';
import { format, parseISO, addDays } from 'date-fns';
import { DesignMaterial } from '../api/designMaterials';
import api from '../utils/axios';
import { GARMENT_SIZES } from '../constants/garmentConstants';
import { generateJobWorkChallan } from '../utils/pdfGenerator';

interface Material {
  materialId: number;
  quantity: number;
  uom: string;
  comments: string;
  rate: number;
}

interface DesignDetail {
  garmentPart: string;
  size: string;
  pieces: number;
  rate: number;
  comments: string;
}

interface FormData {
  jobTypeId: string;
  vendorId: string;
  designNo: string;
  isSampleGiven: boolean;
  garment_part: string;
  issueDate: string;
  expectedReturn: string;
  notes: string;
  materials: Material[];
  designDetails: DesignDetail[];
  image1_url: string;
  image2_url: string;
}

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const CreateJobWorkPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;
  const prefilledDesignNo = searchParams.get('designNo');
  const returnTo = searchParams.get('returnTo') || '/job-works';

  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [vendors, setVendors] = useState<any[]>([]);
  const [jobTypes, setJobTypes] = useState<IJobWorkType[]>([]);
  const [designs, setDesigns] = useState<any[]>([]);
  const [designMaterials, setDesignMaterials] = useState<DesignMaterial[]>([]);
  const [availableMaterials, setAvailableMaterials] = useState<any[]>([]);
  const [jobWorkGarmentParts, setJobWorkGarmentParts] = useState<{ id: number; name: string; isActive: boolean }[]>([]);

  // Form data
  const [form, setForm] = useState<FormData>({
    jobTypeId: '',
    vendorId: '',
    designNo: '',
    isSampleGiven: false,
    garment_part: '',
    issueDate: format(new Date(), 'yyyy-MM-dd'),
    expectedReturn: format(addDays(new Date(), 8), 'yyyy-MM-dd'),
    notes: '',
    materials: [],
    designDetails: [],
    image1_url: '',
    image2_url: '',
  });

  // Validation errors
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Save dialog state (Draft vs Issue)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<CreateJobWorkDTO | null>(null);

  // Helper to fetch all designs (paginate through /designs summary)
  const fetchAllDesigns = async (): Promise<any[]> => {
    const pageSize = 200;
    let page = 1;
    let all: any[] = [];
    while (true) {
      const resp = await api.get('/designs', { params: { summary: 1, page, pageSize, withCount: 1 } });
      const data = Array.isArray(resp.data) ? resp.data : [];
      all = all.concat(data);
      const total = Number(resp.headers['x-total-count'] || 0);
      if (total > 0) {
        if (page * pageSize >= total) break;
      } else {
        if (data.length < pageSize) break;
      }
      page += 1;
    }
    return all;
  };

  // Fetch vendors, job types, and designs
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch job types, vendors, designs (all pages), available materials, and garment parts
        const [jobTypesResponse, vendorsResponse, allDesigns, availableMaterialsResponse, garmentPartsResponse] = await Promise.all([
          getJobWorkTypes(),
          getVendors(),
          fetchAllDesigns(),
          api.get('/materials/available'),
          getJobWorkGarmentParts(),
        ]);

        setJobTypes(jobTypesResponse.data);
        setVendors(vendorsResponse.data);
        setDesigns(allDesigns);
        setAvailableMaterials(availableMaterialsResponse.data || []);
        setJobWorkGarmentParts((garmentPartsResponse.data || []).filter((p: any) => p.isActive));

        // Pre-fill design number if coming from designs page
        if (prefilledDesignNo) {
          setForm(prev => ({
            ...prev,
            designNo: prefilledDesignNo,
          }));
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load form data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [prefilledDesignNo]);

  // Fetch job work data for editing
  useEffect(() => {
    const fetchJobWorkForEdit = async () => {
      if (!isEditing) return;

      try {
        setLoading(true);
        setError(null);

        const jobWorkData = await jobWorksApi.getById(parseInt(id));
        console.log('Job Work Data for edit:', jobWorkData);

        // Format dates
        const formattedIssueDate = jobWorkData.issueDate ? format(parseISO(jobWorkData.issueDate), 'yyyy-MM-dd') : '';
        const formattedExpectedReturn = jobWorkData.expectedReturn ? format(parseISO(jobWorkData.expectedReturn), 'yyyy-MM-dd') : '';

        // First fetch design materials for this design to ensure they're available
        if (jobWorkData.designNo) {
          const response = await api.get(`/design-materials/design/${jobWorkData.designNo}`);
          console.log('Design Materials:', response.data);
          setDesignMaterials(response.data);

          // Then map materials from the job work
          const materials = jobWorkData.materials.map((material: any) => ({
            materialId: material.materialId,
            quantity: material.quantity,
            uom: material.uom,
            comments: material.comments || '',
            rate: material.rate || 0
          }));

          console.log('Mapped materials:', materials);

          const designDetails = jobWorkData.designDetails.map((detail: any) => ({
            garmentPart: detail.garmentPart,
            size: detail.size,
            pieces: detail.pieces,
            rate: detail.rate || 0,
            comments: detail.comments || ''
          }));

          // Set form data from job work
          setForm({
            jobTypeId: jobWorkData.jobTypeId.toString(),
            vendorId: jobWorkData.vendorId.toString(),
            designNo: jobWorkData.designNo,
            isSampleGiven: jobWorkData.isSampleGiven,
            garment_part: (jobWorkData as any).garment_part || '',
            issueDate: formattedIssueDate,
            expectedReturn: formattedExpectedReturn,
            notes: jobWorkData.notes || '',
            materials,
            designDetails,
            image1_url: (jobWorkData as any).image1_url || '',
            image2_url: (jobWorkData as any).image2_url || '',
          });
        }
      } catch (err) {
        console.error('Error fetching job work for editing:', err);
        setError('Failed to load job work data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobWorkForEdit();
  }, [id, isEditing]);

  // Fetch design materials when design is selected
  useEffect(() => {
    const fetchDesignMaterials = async () => {
      if (!form.designNo) {
        setDesignMaterials([]);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/design-materials/design/${form.designNo}`);
        setDesignMaterials(response.data);
      } catch (err) {
        console.error('Error fetching design materials:', err);
        setError('Failed to load design materials. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDesignMaterials();
  }, [form.designNo]);

  // Debug materials
  useEffect(() => {
    if (isEditing && form.materials.length > 0 && designMaterials.length > 0) {
      console.log('Current form materials:', form.materials);
      console.log('Current design materials:', designMaterials);

      // Check if materials in form have matching design materials
      form.materials.forEach((material, index) => {
        const match = designMaterials.find(m => m.material_id === material.materialId);
        console.log(`Material ${index} (${material.materialId}) match:`, match ? 'Found' : 'Not found');
      });
    }
  }, [form.materials, designMaterials, isEditing]);

  // Handle form field changes
  const handleChange = (field: string, value: any) => {
    setForm({
      ...form,
      [field]: value
    });

    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  // Handle date changes
  const handleDateChange = (field: string, date: Date | null) => {
    if (date) {
      handleChange(field, format(date, 'yyyy-MM-dd'));
    } else {
      handleChange(field, '');
    }
  };

  // Add a new material
  const handleAddMaterial = () => {
    setForm({
      ...form,
      materials: [
        ...form.materials,
        {
          materialId: 0, // No default selection
          quantity: 0,
          uom: '',
          comments: '',
          rate: 0
        }
      ]
    });
  };

  // Remove a material
  const handleRemoveMaterial = (index: number) => {
    const updatedMaterials = [...form.materials];
    updatedMaterials.splice(index, 1);

    setForm({
      ...form,
      materials: updatedMaterials
    });
  };

  // Derived flags from selected job type's output_type
  const selectedJobType = jobTypes.find(t => t.id === parseInt(form.jobTypeId));
  const isProcessOnly = selectedJobType?.output_type === 'PROCESS_ONLY';

  // Both sections always enabled for all job types
  const materialsDisabled = false;
  const designDetailsDisabled = false;

  // Rate mutual exclusivity: rates can only be entered in one section
  const hasMaterialRate = form.materials.some(m => m.rate > 0);
  const hasDesignDetailRate = form.designDetails.some(d => d.rate > 0);
  const materialRateDisabled = hasDesignDetailRate;
  const designDetailRateDisabled = hasMaterialRate;

  // Auto-fill from stitching state
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [autoFillStatus, setAutoFillStatus] = useState<string | null>(null);

  // Auto-fill design details from stitched pieces (for PROCESS_ONLY)
  const handleAutoFillFromStitching = useCallback(async (dNo: string, gPart: string) => {
    if (!dNo || !gPart) return;
    setAutoFillLoading(true);
    setAutoFillStatus(null);
    try {
      const res = await api.get('/worker-assignments/capacity', {
        params: { design_no: dNo, garment_part: gPart, jobType: 'STITCHING' }
      });
      const capacity = res.data.capacity;
      const newDetails: DesignDetail[] = Object.entries(capacity)
        .filter(([, v]: any) => v.goodReceived > 0)
        .map(([size, v]: any) => ({
          garmentPart: gPart, size, pieces: v.goodReceived, rate: 0, comments: '',
        }));
      if (newDetails.length > 0) {
        setForm(prev => ({ ...prev, designDetails: newDetails }));
        const totalPcs = newDetails.reduce((s, d) => s + d.pieces, 0);
        setAutoFillStatus(`Auto-filled ${newDetails.length} size(s) with ${totalPcs} stitched pieces`);
      } else {
        setAutoFillStatus('No stitched pieces found for this design + garment part');
      }
    } catch {
      setAutoFillStatus('Could not load stitching data');
    } finally {
      setAutoFillLoading(false);
    }
  }, []);

  // Helper to get unique materials with total allocated_qty
  const getUniqueMaterialOptions = () => {
    // Start with allocated materials for this design
    const materialMap = new Map<number, any>();

    // For PROCESS_ONLY, skip design material allocations and only show SEMI_FINISHED inventory
    if (!isProcessOnly) {
      designMaterials.forEach(dm => {
        if (!dm.material) return;
        const key = dm.material_id as number;
        const existing = materialMap.get(key);
        const consolidated = existing || {
          material_id: dm.material_id,
          material: dm.material,
          allocated_qty: 0,
          available_qty: dm.material?.available_qty ?? 0,
        };
        consolidated.allocated_qty += dm.allocated_qty;
        materialMap.set(key, consolidated);
      });
    }

    // Add all available inventory (>0) not yet in map
    availableMaterials
      .filter((m: any) => (m.available_qty ?? 0) > 0)
      .forEach((m: any) => {
        const key = m.id as number;
        if (!materialMap.has(key)) {
          materialMap.set(key, {
            material_id: key,
            material: {
              id: key,
              material_code: m.material_code,
              uom: m.uom,
              material_type: m.material_type,
              image_url: m.image_url || null
            },
            allocated_qty: 0,
            available_qty: m.available_qty
          });
        } else {
          // Ensure available_qty reflects latest inventory for allocated items too
          const entry = materialMap.get(key);
          entry.available_qty = m.available_qty;
          if (!entry.material?.image_url && m.image_url) {
            entry.material.image_url = m.image_url;
          }
          materialMap.set(key, entry);
        }
      });

    return Array.from(materialMap.values());
  };

  // Update a material
  const handleMaterialChange = (index: number, field: string, value: any) => {
    const updatedMaterials = [...form.materials];
    let materialError = '';

    // Removed front-end cap based on allocated quantity; backend will auto-allocate from available stock

    // If materialId is being updated, also update the UOM if available
    if (field === 'materialId') {
      const selectedAlloc = designMaterials.find(m => m.material_id === value);
      const selectedInv = availableMaterials.find((m: any) => m.id === value);
      const uom = selectedAlloc?.material?.uom || selectedInv?.uom || '';
      updatedMaterials[index] = {
        ...updatedMaterials[index],
        materialId: value,
        uom
      };
    } else {
      updatedMaterials[index] = {
        ...updatedMaterials[index],
        [field]: field === 'quantity' ? parseFloat(value) : value
      };
    }

    setForm({
      ...form,
      materials: updatedMaterials
    });

    // Set or clear error for this material
    setErrors(prev => ({
      ...prev,
      [`materials.${index}.quantity`]: materialError
    }));
  };

  // Add a new design detail
  const handleAddDesignDetail = () => {
    setForm({
      ...form,
      designDetails: [
        ...form.designDetails,
        {
          garmentPart: '',
          size: '',
          pieces: 0,
          rate: 0,
          comments: ''
        }
      ]
    });
  };

  // Remove a design detail
  const handleRemoveDesignDetail = (index: number) => {
    const updatedDesignDetails = [...form.designDetails];
    updatedDesignDetails.splice(index, 1);

    setForm({
      ...form,
      designDetails: updatedDesignDetails
    });
  };

  // Size sequence for auto-fill
  const SIZE_SEQUENCE = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

  // Update a design detail with auto-fill logic
  const handleDesignDetailChange = (index: number, field: string, value: any) => {
    const updatedDesignDetails = [...form.designDetails];
    updatedDesignDetails[index] = {
      ...updatedDesignDetails[index],
      [field]: value
    };

    // Auto-fill logic for garment part
    if (field === 'garmentPart' && value) {
      // Auto-fill garment part for all subsequent rows that have blank garment part
      for (let i = index + 1; i < updatedDesignDetails.length; i++) {
        if (!updatedDesignDetails[i].garmentPart) {
          updatedDesignDetails[i] = {
            ...updatedDesignDetails[i],
            garmentPart: value
          };
        }
      }
    }

    // Auto-fill logic for size
    if (field === 'size' && value) {
      const startSizeIndex = SIZE_SEQUENCE.indexOf(value);
      if (startSizeIndex !== -1) {
        // Auto-fill subsequent sizes for rows that have blank size
        let sizeOffset = 1;
        for (let i = index + 1; i < updatedDesignDetails.length; i++) {
          if (!updatedDesignDetails[i].size) {
            const nextSizeIndex = startSizeIndex + sizeOffset;
            if (nextSizeIndex < SIZE_SEQUENCE.length) {
              updatedDesignDetails[i] = {
                ...updatedDesignDetails[i],
                size: SIZE_SEQUENCE[nextSizeIndex]
              };
              sizeOffset++;
            }
          }
        }
      }
    }

    setForm({
      ...form,
      designDetails: updatedDesignDetails
    });
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!form.vendorId) {
      newErrors.vendorId = 'Vendor is required';
    }

    if (!form.jobTypeId) {
      newErrors.jobTypeId = 'Job type is required';
    }

    if (!form.designNo) {
      newErrors.designNo = 'Design number is required';
    }

    if (!form.garment_part) {
      newErrors.garment_part = 'Garment part is required';
    }

    // Validate based on output type
    const hasDesignDetails = form.designDetails && form.designDetails.length > 0 &&
      form.designDetails.some(detail => detail.garmentPart && detail.size && detail.pieces > 0);
    const hasMaterials = form.materials && form.materials.length > 0 &&
      form.materials.some(material => material.materialId > 0 && material.quantity > 0);

    if (isProcessOnly) {
      // PROCESS_ONLY: either materials or design details required
      if (!hasMaterials && !hasDesignDetails) {
        newErrors.designDetails = 'Either materials or design details are required';
      }
    } else {
      // CREATES_INVENTORY: design details always required (creates semi-finished inventory)
      if (!hasDesignDetails) {
        newErrors.designDetails = 'Design details with pieces are required';
      }
    }

    // Removed client-side cap against allocated_qty; backend enforces inventory availability and auto-allocates

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission - validates and shows Draft/Issue dialog for new job works
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      setError('Please fix the errors before submitting');
      return;
    }

    // Filter out empty/invalid rows before submission
    const validMaterials = form.materials.filter(m => m.materialId > 0 && m.quantity > 0);
    const validDesignDetails = form.designDetails.filter(d => d.garmentPart && d.size && d.pieces > 0);

    // processMaterial is true when rates are entered on materials
    // This controls receipt mode: material qty vs pieces
    const derivedProcessMaterial = hasMaterialRate;

    // Transform form data to match API expectations
    const jobWorkData: CreateJobWorkDTO = {
      vendorId: parseInt(form.vendorId),
      jobTypeId: parseInt(form.jobTypeId),
      designNo: form.designNo,
      issueDate: form.issueDate,
      expectedReturn: form.expectedReturn || undefined,
      isSampleGiven: form.isSampleGiven,
      processMaterial: derivedProcessMaterial,
      garment_part: form.garment_part || null,
      notes: form.notes || undefined,
      materials: validMaterials.map(material => ({
        materialId: material.materialId,
        quantity: material.quantity,
        uom: material.uom,
        rate: material.rate,
        comments: material.comments || undefined
      })),
      designDetails: validDesignDetails.map(detail => ({
        garmentPart: detail.garmentPart,
        size: detail.size,
        pieces: detail.pieces,
        rate: detail.rate,
        comments: detail.comments || undefined
      })),
      image1_url: form.image1_url || undefined,
      image2_url: form.image2_url || undefined,
    } as any;

    if (isEditing) {
      // For editing, save directly without dialog
      await saveJobWork(jobWorkData, false);
    } else {
      // For new job works, show Draft/Issue dialog
      setPendingFormData(jobWorkData);
      setSaveDialogOpen(true);
    }
  };

  // Save job work with optional issue to vendor
  const saveJobWork = async (jobWorkData: CreateJobWorkDTO, issueToVendor: boolean) => {
    try {
      setLoading(true);
      setSaveDialogOpen(false);

      let response;
      if (isEditing) {
        // Update existing job work
        response = await jobWorksApi.update(parseInt(id), jobWorkData);
        setSuccess('Job Work updated successfully');
      } else {
        // Create new job work
        response = await jobWorksApi.create(jobWorkData);
        
        if (issueToVendor) {
          // Issue to vendor - update status to ISSUED
          await jobWorksApi.issue(response.id, { issue_date: format(new Date(), 'yyyy-MM-dd') });
          setSuccess('Job Work created and issued to vendor!');
          
          // Generate and open PDF challan
          await generateChallanPDF(response.id);
        } else {
          setSuccess('Job Work saved as draft');
        }
      }

      // Redirect after a short delay
      setTimeout(() => {
        navigate(returnTo);
      }, 1500);
    } catch (error: any) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} job work:`, error);
      const errorMessage = error.response?.data?.error || `Failed to ${isEditing ? 'update' : 'create'} Job Work. Please try again.`;
      setError(errorMessage);
    } finally {
      setLoading(false);
      setPendingFormData(null);
    }
  };

  // Generate and open PDF challan for a job work
  const generateChallanPDF = async (jobWorkId: number) => {
    try {
      // Fetch the full job work details
      const jobWork = await jobWorksApi.getById(jobWorkId);
      
      // Transform materials for PDF
      const materials = jobWork.materials.map((material: any) => ({
        materialCode: material.material?.material_code || 'N/A',
        materialType: material.material?.material_type || 'N/A',
        materialImage: material.material?.image_url || null,
        quantity: material.quantity || 0,
        uom: material.uom || 'PCS',
        rate: material.rate || material.processingRate || 0,
        comments: material.comments || ''
      }));

      // Calculate sizes and total quantity
      const sizes = jobWork.designDetails.map((detail: any) => ({
        size: detail.size || '',
        quantity: detail.pieces || 0
      }));
      const totalQuantity = sizes.reduce((sum: number, item: any) => sum + item.quantity, 0);

      // Prepare challan data
      const challanData: any = {
        jobWorkCode: jobWork.jobWorkCode || '',
        designNo: jobWork.designNo || '',
        issueDate: jobWork.issueDate ? format(new Date(jobWork.issueDate), 'dd/MM/yyyy') : 'N/A',
        expectedReturn: jobWork.expectedReturn ? format(new Date(jobWork.expectedReturn), 'dd/MM/yyyy') : 'N/A',
        jobberName: jobWork.vendor?.registered_name || jobWork.vendor?.alias || 'Unknown',
        jobberPhone: jobWork.vendor?.phone || 'N/A',
        jobberAddress: jobWork.vendor?.address || 'N/A',
        jobType: {
          name: jobWork.jobType?.name || 'N/A',
          description: jobWork.jobType?.description
        },
        vendor: {
          registered_name: jobWork.vendor?.registered_name || 'Unknown',
          alias: jobWork.vendor?.alias || '',
          address: jobWork.vendor?.address || '',
          phone: jobWork.vendor?.phone || ''
        },
        companyName: (jobWork.company && jobWork.company.name) || 'STITCHFLO',
        materials: materials,
        designDetails: jobWork.designDetails.map((detail: any) => ({
          garmentPart: detail.garmentPart || '',
          size: detail.size || '',
          pieces: detail.pieces || 0,
          rate: detail.rate || 0
        })),
        sizes: sizes,
        totalQuantity: totalQuantity,
        notes: jobWork.notes || undefined,
        designImage: jobWork.design?.image_url || undefined,
        companyDetails: {
          brandName: jobWork.company?.brand_name || '',
          name: jobWork.company?.name || '',
          address: jobWork.company?.address || '',
          contactNumber: jobWork.company?.contact_number || ''
        },
        processMaterial: jobWork.processMaterial,
        image1_url: jobWork.image1_url || undefined,
        image2_url: jobWork.image2_url || undefined,
      };

      // Generate the PDF
      await generateJobWorkChallan(challanData);
    } catch (err) {
      console.error('Error generating challan PDF:', err);
      // Don't throw - PDF generation failure shouldn't block the flow
    }
  };

  // Handle Draft button click
  const handleSaveAsDraft = () => {
    if (pendingFormData) {
      saveJobWork(pendingFormData, false);
    }
  };

  // Handle Issue to Vendor button click
  const handleIssueToVendor = () => {
    if (pendingFormData) {
      saveJobWork(pendingFormData, true);
    }
  };

  // Handle back button
  const handleBack = () => {
    navigate(returnTo);
  };

  // Selected design for showing thumbnail in Design Details grid
  const selectedDesign = designs.find(d => d.design_no === form.designNo) || null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={600}>
              {isEditing ? 'Edit Job Work' : 'New Job Work'}
            </Typography>
          </Box>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading && !form.vendorId ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={2} sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                {/* Section: Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Basic Information
                  </Typography>
                  <Divider />
                </Grid>

                {/* Row 1: Job Type | Vendor | Design Number */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth error={!!errors.jobTypeId} size="small">
                    <InputLabel>Job Type *</InputLabel>
                    <Select
                      value={form.jobTypeId}
                      onChange={(e) => handleChange('jobTypeId', e.target.value)}
                      label="Job Type *"
                    >
                      {jobTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.jobTypeId && (
                      <FormHelperText>{errors.jobTypeId}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth error={!!errors.vendorId} size="small">
                    <InputLabel>Vendor *</InputLabel>
                    <Select
                      value={form.vendorId}
                      onChange={(e) => handleChange('vendorId', e.target.value)}
                      label="Vendor *"
                    >
                      {vendors.map((vendor) => (
                        <MenuItem key={vendor.id} value={vendor.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{vendor.registered_name}</span>
                            <Chip
                              size="small"
                              label={vendor.is_internal ? 'Internal' : 'External'}
                              color={vendor.is_internal ? 'success' : 'default'}
                              variant={vendor.is_internal ? 'filled' : 'outlined'}
                              sx={{ minWidth: 55, fontSize: '0.65rem', height: 20 }}
                            />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.vendorId && (
                      <FormHelperText>{errors.vendorId}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    size="small"
                    value={designs.find(d => d.design_no === form.designNo) || null}
                    onChange={(event, newValue) => {
                      handleChange('designNo', newValue ? newValue.design_no : '');
                    }}
                    options={designs}
                    getOptionLabel={(option) => option.design_no}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Design Number *"
                        error={!!errors.designNo}
                        helperText={errors.designNo}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {option.image_url && (
                          <Box
                            component="img"
                            src={option.image_url}
                            alt={option.design_no}
                            sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 1 }}
                          />
                        )}
                        <Box>
                          <Typography variant="body2">{option.design_no}</Typography>
                          {option.name && (
                            <Typography variant="caption" color="text.secondary">
                              {option.name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>

                {/* Row 2: Garment Part | Issue Date | Expected Return */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth required error={!!errors.garment_part} size="small">
                    <InputLabel>Garment Part *</InputLabel>
                    <Select
                      value={form.garment_part}
                      onChange={(e) => handleChange('garment_part', e.target.value)}
                      label="Garment Part *"
                    >
                      {jobWorkGarmentParts.map((part) => (
                        <MenuItem key={part.id} value={part.name}>{part.name}</MenuItem>
                      ))}
                    </Select>
                    {errors.garment_part && (
                      <FormHelperText>{errors.garment_part}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={6} md={4}>
                  <DatePicker
                    label="Issue Date"
                    value={form.issueDate ? new Date(form.issueDate) : null}
                    onChange={(date) => handleDateChange('issueDate', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        error: !!errors.issueDate,
                        helperText: errors.issueDate
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={6} sm={6} md={4}>
                  <DatePicker
                    label="Expected Return"
                    value={form.expectedReturn ? new Date(form.expectedReturn) : null}
                    onChange={(date) => handleDateChange('expectedReturn', date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small'
                      }
                    }}
                  />
                </Grid>

                {/* Row 3: Sample Given */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isSampleGiven}
                        onChange={(e) => handleChange('isSampleGiven', e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Sample Given</Typography>}
                  />
                </Grid>

                {/* Section: Materials */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {isProcessOnly ? 'Materials for Processing' : 'Materials'}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddMaterial}
                      disabled={materialsDisabled}
                    >
                      Add Material
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />

                  {isProcessOnly && (
                    <Alert severity="info" sx={{ mb: 1.5 }} variant="outlined">
                      Select materials being sent for processing. Quantity will be reduced on issue and restored on receipt.
                    </Alert>
                  )}

                  {errors.materials && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {errors.materials}
                    </Alert>
                  )}

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell width="35%" sx={{ fontWeight: 600 }}>Material</TableCell>
                          <TableCell width="12%" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                          <TableCell width="12%" sx={{ fontWeight: 600 }}>Rate</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Taka Details</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, width: 60 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {form.materials.map((material, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {getUniqueMaterialOptions().length > 0 ? (
                                <Autocomplete
                                  size="small"
                                  fullWidth
                                  disabled={materialsDisabled}
                                  options={getUniqueMaterialOptions()}
                                  getOptionLabel={(option: any) => {
                                    const code = option.material?.material_code || '';
                                    const type = option.material?.material_type || '';
                                    const uom = option.material?.uom ? ` ${option.material.uom}` : '';
                                    const alloc = option.allocated_qty ? `, alloc: ${option.allocated_qty}` : '';
                                    return `${code} - ${type} (avail: ${option.available_qty}${uom}${alloc})`;
                                  }}
                                  isOptionEqualToValue={(opt: any, val: any) => opt.material_id === val.material_id}
                                  value={getUniqueMaterialOptions().find((o: any) => o.material_id === material.materialId) || null}
                                  onChange={(_e, newValue: any | null) => {
                                    const selectedId = newValue ? Number(newValue.material_id) : 0;
                                    handleMaterialChange(index, 'materialId', selectedId);
                                  }}
                                  renderOption={(props, option: any) => (
                                    <li {...props}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {option.material?.image_url && (
                                          <Box component="img" src={option.material.image_url} alt="" sx={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 0.5 }} />
                                        )}
                                        <Typography variant="body2">
                                          {option.material?.material_code} - {option.material?.material_type} (avail: {option.available_qty} {option.material?.uom}{option.allocated_qty ? `, alloc: ${option.allocated_qty}` : ''})
                                        </Typography>
                                      </Box>
                                    </li>
                                  )}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select material"
                                      error={!!errors[`materials.${index}.materialId`]}
                                      helperText={errors[`materials.${index}.materialId`]}
                                    />
                                  )}
                                />
                              ) : (
                                <TextField disabled value="No materials available" fullWidth size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                disabled={materialsDisabled}
                                value={material.quantity}
                                onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                                placeholder={material.uom}
                                InputProps={{
                                  inputProps: { min: 0, step: material.uom === 'mtr' ? 0.01 : 1 }
                                }}
                                fullWidth
                                error={!!errors[`materials.${index}.quantity`]}
                                helperText={errors[`materials.${index}.quantity`]}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                disabled={materialRateDisabled}
                                value={material.rate}
                                onChange={(e) => handleMaterialChange(index, 'rate', Number(e.target.value))}
                                placeholder="Rate"
                                InputProps={{
                                  inputProps: { min: 0, step: 0.01 }
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                disabled={materialsDisabled}
                                value={material.comments}
                                onChange={(e) => handleMaterialChange(index, 'comments', e.target.value)}
                                placeholder="Taka details"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveMaterial(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Section: Design Details */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Design Details
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {isProcessOnly && form.designNo && form.garment_part && (
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          disabled={autoFillLoading || designDetailsDisabled}
                          onClick={() => handleAutoFillFromStitching(form.designNo, form.garment_part)}
                        >
                          {autoFillLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                          Auto-fill from Stitching
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddDesignDetail}
                        disabled={designDetailsDisabled}
                      >
                        Add Row
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />

                  {autoFillStatus && (
                    <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setAutoFillStatus(null)}>
                      {autoFillStatus}
                    </Alert>
                  )}

                  {errors.designDetails && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {errors.designDetails}
                    </Alert>
                  )}

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          {selectedDesign?.image_url && (
                            <TableCell sx={{ width: 60, fontWeight: 600 }}>Design</TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 600, width: '35%' }}>Garment Part</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: '20%' }}>Size</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: '15%' }}>Pieces</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: '15%' }}>Rate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, width: 60 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {form.designDetails.map((detail, index) => (
                          <TableRow key={index}>
                            {selectedDesign?.image_url && (
                              <TableCell sx={{ py: 0.5 }}>
                                <Box
                                  component="img"
                                  src={selectedDesign.image_url}
                                  alt={selectedDesign.design_no}
                                  sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <FormControl fullWidth size="small" disabled={designDetailsDisabled}>
                                <Select
                                  value={detail.garmentPart}
                                  onChange={(e) => handleDesignDetailChange(index, 'garmentPart', e.target.value)}
                                  displayEmpty
                                >
                                  <MenuItem value="" disabled><em>Select part</em></MenuItem>
                                  {jobWorkGarmentParts.map((part) => (
                                    <MenuItem key={part.id} value={part.name}>
                                      {part.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <FormControl fullWidth size="small" disabled={designDetailsDisabled}>
                                <Select
                                  value={detail.size}
                                  onChange={(e) => handleDesignDetailChange(index, 'size', e.target.value)}
                                  displayEmpty
                                >
                                  <MenuItem value="" disabled><em>Size</em></MenuItem>
                                  {GARMENT_SIZES.map((size) => (
                                    <MenuItem key={size} value={size}>
                                      {size}
                                    </MenuItem>
                                  ))}
                                  <MenuItem value={'Free Size'}>Free Size</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                disabled={designDetailsDisabled}
                                value={detail.pieces}
                                onChange={(e) => handleDesignDetailChange(index, 'pieces', Number(e.target.value))}
                                InputProps={{
                                  inputProps: { min: 0 }
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                size="small"
                                disabled={designDetailRateDisabled}
                                value={detail.rate}
                                onChange={(e) => handleDesignDetailChange(index, 'rate', Number(e.target.value))}
                                InputProps={{
                                  inputProps: { min: 0, step: 0.01 }
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveDesignDetail(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mt: 1 }}>
                    Notes / Remarks
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Box sx={{ '.ql-container': { minHeight: 80 }, '.ql-editor': { minHeight: 80 } }}>
                    <ReactQuill
                      theme="snow"
                      value={form.notes}
                      onChange={(value) => handleChange('notes', value)}
                      placeholder="Any special instructions for the vendor..."
                      modules={quillModules}
                    />
                  </Box>
                </Grid>

                {/* Attachments */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mt: 1 }}>
                    Attachments
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {[
                      { key: 'image1_url' as const, label: 'Image 1' },
                      { key: 'image2_url' as const, label: 'Image 2' },
                    ].map(({ key, label }) => (
                      <Box key={key} sx={{ width: 120 }}>
                        {form[key] ? (
                          <Box sx={{ position: 'relative' }}>
                            <Box
                              component="img"
                              src={form[key]}
                              alt={label}
                              sx={{ width: 120, height: 150, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                            />
                            <IconButton
                              size="small"
                              sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: '#fff', '&:hover': { bgcolor: 'error.dark' }, width: 24, height: 24 }}
                              onClick={() => handleChange(key, '')}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Button
                            variant="outlined"
                            component="label"
                            sx={{ width: 120, height: 150, display: 'flex', flexDirection: 'column', gap: 0.5, textTransform: 'none', borderStyle: 'dashed' }}
                          >
                            <AddIcon />
                            <Typography variant="caption">{label}</Typography>
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  const res = await api.post('/upload?module=job-works', formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  handleChange(key, res.data.url);
                                } catch (err) {
                                  console.error('Image upload failed:', err);
                                }
                              }}
                            />
                          </Button>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Grid>

                {/* Submit */}
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={handleBack}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={22} /> : (isEditing ? 'Update Job Work' : 'Create Job Work')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}
      </Box>

      {/* Draft/Issue Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        aria-labelledby="save-dialog-title"
      >
        <DialogTitle id="save-dialog-title">
          Save Job Work
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            How would you like to save this job work?
          </DialogContentText>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Save as Draft:</strong> Job work will be saved but not issued to vendor. You can edit and issue later.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Issue to Vendor:</strong> Job work will be issued immediately and a PDF challan will be generated for download.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAsDraft}
            variant="outlined"
            disabled={loading}
          >
            Save as Draft
          </Button>
          <Button 
            onClick={handleIssueToVendor}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Issue to Vendor'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateJobWorkPage; 